import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Phone, LogOut, Package, Clock, MapPin, ChevronRight, Gift, Copy, Check, Share2, Award } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const statusLabel = (s) => ({
  pending: 'Received', confirmed: 'Confirmed', preparing: 'Preparing',
  ready: 'Ready', out_for_delivery: 'On the Way', delivered: 'Delivered', cancelled: 'Cancelled'
}[s] || s);

const statusColor = (s) => ({
  pending: '#C97A2B', confirmed: '#C97A2B', preparing: '#C97A2B',
  ready: '#6B705C', out_for_delivery: '#B65A3A', delivered: '#2F2F2F', cancelled: '#B65A3A'
}[s] || '#6B705C');

const CustomerPage = () => {
  const [phone, setPhone] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [referralInfo, setReferralInfo] = useState(null);
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();

  const fetchReferralInfo = async (phoneNum) => {
    try {
      const loginRes = await axios.post(`${API}/api/customers/phone-login`, { phone: phoneNum });
      const cust = loginRes.data?.customer;
      if (!cust?.id) return;
      const res = await axios.get(`${API}/api/customers/${cust.id}/referral-info`);
      setReferralInfo({ ...res.data, points: cust.loyalty_points });
    } catch {}
  };

  // Check if already logged in
  useEffect(() => {
    const saved = localStorage.getItem('sunsari_customer_phone');
    if (saved) {
      setPhone(saved);
      setIsLoggedIn(true);
      fetchOrders(saved);
      fetchReferralInfo(saved);
    }
  }, []);

  const fetchOrders = async (phoneNum) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/api/orders/lookup`, { params: { phone: phoneNum } });
      setOrders(res.data.orders || []);
    } catch {
      setError('Could not load orders.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!phone.trim()) { setError('Please enter your phone number'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/api/orders/lookup`, { params: { phone } });
      const found = res.data.orders || [];
      if (found.length === 0) {
        setError('No orders found for this number. Place an order first!');
        setLoading(false);
        return;
      }
      localStorage.setItem('sunsari_customer_phone', phone);
      setOrders(found);
      setIsLoggedIn(true);
      fetchReferralInfo(phone);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('sunsari_customer_phone');
    setIsLoggedIn(false);
    setOrders([]);
    setPhone('');
    setSelectedOrder(null);
  };

  const activeOrders = orders.filter(o => !['delivered', 'cancelled'].includes(o.status));
  const pastOrders = orders.filter(o => ['delivered', 'cancelled'].includes(o.status));

  // Login screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-[#F5F3EF] flex items-center justify-center px-4" data-testid="customer-login">
        <motion.div className="w-full max-w-sm" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#C97A2B]/10 flex items-center justify-center">
              <Phone className="w-7 h-7 text-[#C97A2B]" />
            </div>
            <h1 className="text-2xl text-[#2F2F2F] mb-2" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>
              My <span className="text-[#C97A2B] italic">Orders</span>
            </h1>
            <p className="text-[#6B705C] text-sm">Sign in with the phone number you used when ordering.</p>
          </div>

          <form onSubmit={handleLogin} className="bg-white rounded-xl border border-[#D9D9D6] p-6 shadow-sm">
            <label className="block text-[10px] tracking-[0.2em] uppercase text-[#6B705C] mb-2">Phone Number</label>
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
              placeholder="+358 40 123 4567"
              className="w-full px-4 py-3 border border-[#D9D9D6] rounded-lg text-[#2F2F2F] placeholder-[#D9D9D6] focus:outline-none focus:border-[#C97A2B] transition-colors text-sm mb-4"
              data-testid="customer-phone-input" />
            {error && <p className="text-[#B65A3A] text-sm mb-3" data-testid="customer-error">{error}</p>}
            <button type="submit" disabled={loading}
              className="w-full py-3 bg-[#2F2F2F] text-white text-sm font-medium rounded-lg hover:bg-[#C97A2B] transition-colors disabled:opacity-50"
              data-testid="customer-login-btn">
              {loading ? 'Loading...' : 'View My Orders'}
            </button>
          </form>

          <div className="text-center mt-6">
            <button onClick={() => navigate('/order')} className="text-[#C97A2B] text-sm hover:underline" data-testid="go-order-btn">
              Place a new order →
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  // Order detail view
  if (selectedOrder) {
    const o = selectedOrder;
    return (
      <div className="min-h-screen bg-[#F5F3EF]" data-testid="order-detail-view">
        <div className="max-w-2xl mx-auto px-4 py-8">
          <button onClick={() => setSelectedOrder(null)} className="text-[#6B705C] text-sm mb-6 hover:text-[#C97A2B] flex items-center gap-1" data-testid="back-to-orders">
            <ChevronRight className="w-4 h-4 rotate-180" /> Back to orders
          </button>

          <div className="bg-white rounded-xl border border-[#D9D9D6] overflow-hidden">
            <div className="p-5 border-b border-[#D9D9D6]/50 flex items-center justify-between">
              <div>
                <p className="text-[10px] tracking-[0.2em] uppercase text-[#6B705C]">Order</p>
                <p className="text-lg font-medium text-[#2F2F2F]" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>
                  #{o.id?.slice(0, 8).toUpperCase()}
                </p>
              </div>
              <span className="px-3 py-1 rounded-full text-[10px] tracking-wide uppercase font-medium"
                style={{ backgroundColor: `${statusColor(o.status)}15`, color: statusColor(o.status) }}>
                {statusLabel(o.status)}
              </span>
            </div>

            {/* Status progress */}
            {!['delivered', 'cancelled'].includes(o.status) && (
              <div className="px-5 py-4 bg-[#F5F3EF]/50">
                <div className="flex items-center justify-between">
                  {['pending', 'confirmed', 'preparing', 'ready', 'out_for_delivery', 'delivered'].map((step, i, arr) => {
                    const currentIdx = arr.indexOf(o.status);
                    const done = i <= currentIdx;
                    return (
                      <React.Fragment key={step}>
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-medium ${done ? 'bg-[#C97A2B] text-white' : 'bg-[#D9D9D6] text-white'}`}>
                          {done ? '✓' : i + 1}
                        </div>
                        {i < arr.length - 1 && <div className={`flex-1 h-[2px] mx-1 ${i < currentIdx ? 'bg-[#C97A2B]' : 'bg-[#D9D9D6]'}`} />}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Items */}
            <div className="p-5 space-y-3">
              {o.items?.map((item, i) => (
                <div key={i} className="flex justify-between text-sm">
                  <span className="text-[#2F2F2F]">{item.name} <span className="text-[#6B705C]">x{item.quantity}</span></span>
                  <span className="text-[#2F2F2F] font-medium">{item.price}</span>
                </div>
              ))}
            </div>

            <div className="px-5 py-4 bg-[#F5F3EF]/60 border-t border-[#D9D9D6]/50 space-y-1">
              <div className="flex justify-between text-sm text-[#6B705C]"><span>Subtotal</span><span>{o.subtotal}</span></div>
              <div className="flex justify-between text-sm text-[#6B705C]"><span>Delivery</span><span>{o.deliveryFee}</span></div>
              <div className="flex justify-between text-base font-bold text-[#2F2F2F] pt-2 border-t border-[#D9D9D6]/50">
                <span>Total</span>
                <span style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>{o.totalPrice}</span>
              </div>
            </div>

            <div className="p-5 border-t border-[#D9D9D6]/50 space-y-2 text-sm">
              {o.customer?.address && <p className="text-[#6B705C]"><MapPin className="w-3.5 h-3.5 inline mr-1" />{o.customer.address}</p>}
              {o.orderDate && <p className="text-[#6B705C]"><Clock className="w-3.5 h-3.5 inline mr-1" />{o.orderDate}</p>}
              <p className="text-[#6B705C]">Payment: {o.paymentMethod || 'Cash on Delivery'}</p>
            </div>
          </div>

          {!['delivered', 'cancelled'].includes(o.status) && (
            <a href="tel:+358403779200"
              className="mt-4 w-full flex items-center justify-center gap-2 py-3 border border-[#D9D9D6] rounded-lg text-sm text-[#2F2F2F] hover:border-[#C97A2B] transition-colors"
              data-testid="call-btn">
              <Phone className="w-4 h-4" /> Call Restaurant
            </a>
          )}
        </div>
      </div>
    );
  }

  // Orders list
  return (
    <div className="min-h-screen bg-[#F5F3EF]" data-testid="customer-dashboard">
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-[#6B705C] text-xs tracking-[0.2em] uppercase">Welcome back</p>
            <h1 className="text-2xl text-[#2F2F2F]" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>
              My <span className="text-[#C97A2B] italic">Orders</span>
            </h1>
            <p className="text-[#6B705C] text-sm mt-1">{phone}</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => fetchOrders(phone)} className="text-[#6B705C] text-xs hover:text-[#C97A2B] transition-colors" data-testid="refresh-btn">
              Refresh
            </button>
            <button onClick={handleLogout} className="flex items-center gap-1 text-[#6B705C] text-xs hover:text-[#B65A3A] transition-colors" data-testid="logout-btn">
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>

        {/* Quick actions */}
        <div className="flex gap-3 mb-8">
          <button onClick={() => navigate('/order')}
            className="flex-1 py-3 bg-[#C97A2B] text-white text-sm font-medium rounded-lg hover:bg-[#B65A3A] transition-colors"
            data-testid="new-order-btn">
            New Order
          </button>
          <button onClick={() => navigate('/combine-tables')}
            className="flex-1 py-3 bg-[#2F2F2F] text-white text-sm font-medium rounded-lg hover:bg-[#C97A2B] transition-colors"
            data-testid="book-table-btn">
            Book Table
          </button>
        </div>

        {/* Refer a Friend Card */}
        {referralInfo && referralInfo.referral_code && (() => {
          const refLink = `${window.location.origin}/order?ref=${referralInfo.referral_code}`;
          const shareText = `Try Sunsari Restaurant in Vaasa — handmade Nepalese curries 🍛. Use my code ${referralInfo.referral_code} on your first order and we both earn loyalty points!`;
          const copyLink = async () => {
            try {
              await navigator.clipboard.writeText(refLink);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            } catch {}
          };
          const waUrl = `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + refLink)}`;
          const smsUrl = `sms:?&body=${encodeURIComponent(shareText + ' ' + refLink)}`;
          const mailUrl = `mailto:?subject=${encodeURIComponent('Try Sunsari Restaurant')}&body=${encodeURIComponent(shareText + '\n\n' + refLink)}`;
          return (
            <div className="mb-8 rounded-2xl overflow-hidden relative bg-gradient-to-br from-[#1A1A1A] via-[#2F2F2F] to-[#1A1A1A] border border-[#C97A2B]/30" data-testid="referral-card">
              <div className="absolute -top-12 -right-12 w-48 h-48 bg-[#C97A2B]/20 rounded-full blur-3xl pointer-events-none" />
              <div className="absolute -bottom-16 -left-12 w-56 h-56 bg-[#B65A3A]/10 rounded-full blur-3xl pointer-events-none" />
              <div className="relative p-6">
                <div className="flex items-center gap-2 mb-2">
                  <Gift className="w-4 h-4 text-[#C97A2B]" />
                  <span className="text-[10px] tracking-[0.3em] uppercase text-[#C97A2B] font-semibold">Refer & Earn</span>
                </div>
                <h2 className="text-white text-xl md:text-2xl leading-tight mb-1" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>
                  Invite friends, earn <span className="italic text-[#C97A2B]">50 points</span>
                </h2>
                <p className="text-white/70 text-xs md:text-sm leading-relaxed mb-5">
                  When a friend places their first order with your code, you get <strong className="text-[#C97A2B]">+50 pts</strong> and they get a <strong className="text-[#C97A2B]">+30 pts welcome bonus</strong>.
                </p>

                {/* Stats Row */}
                <div className="grid grid-cols-3 gap-2 mb-5">
                  <div className="bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-center" data-testid="ref-stat-invited">
                    <p className="text-white text-lg font-semibold leading-none">{referralInfo.invited_total}</p>
                    <p className="text-white/50 text-[9px] uppercase tracking-widest mt-1">Invited</p>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-center" data-testid="ref-stat-successful">
                    <p className="text-[#C97A2B] text-lg font-semibold leading-none">{referralInfo.successful_referrals}</p>
                    <p className="text-white/50 text-[9px] uppercase tracking-widest mt-1">Ordered</p>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-center" data-testid="ref-stat-pending">
                    <p className="text-white/80 text-lg font-semibold leading-none">{referralInfo.pending_referrals}</p>
                    <p className="text-white/50 text-[9px] uppercase tracking-widest mt-1">Pending</p>
                  </div>
                </div>

                {/* Code + Copy */}
                <div className="bg-white/5 border border-[#C97A2B]/30 rounded-xl p-3 mb-4 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-white/50 text-[9px] uppercase tracking-widest mb-1">Your Code</p>
                    <p className="text-[#C97A2B] text-xl md:text-2xl tracking-[0.2em] font-mono font-bold truncate" data-testid="referral-code">
                      {referralInfo.referral_code}
                    </p>
                  </div>
                  <button onClick={copyLink}
                    className="flex items-center gap-1.5 px-3 py-2 bg-[#C97A2B] text-white text-xs font-semibold rounded-lg hover:bg-[#B65A3A] transition-colors flex-shrink-0"
                    data-testid="copy-referral-link-btn">
                    {copied ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy Link</>}
                  </button>
                </div>

                {/* Share Buttons */}
                <div className="grid grid-cols-3 gap-2">
                  <button type="button" onClick={() => window.open(waUrl, '_blank', 'noopener')}
                    className="flex items-center justify-center gap-1.5 py-2.5 bg-[#25D366]/15 border border-[#25D366]/30 text-[#25D366] text-xs font-semibold rounded-lg hover:bg-[#25D366]/25 transition-colors"
                    data-testid="share-whatsapp-btn">
                    <Share2 className="w-3.5 h-3.5" /> WhatsApp
                  </button>
                  <button type="button" onClick={() => { window.location.href = smsUrl; }}
                    className="flex items-center justify-center gap-1.5 py-2.5 bg-white/5 border border-white/15 text-white text-xs font-semibold rounded-lg hover:bg-white/10 transition-colors"
                    data-testid="share-sms-btn">
                    <Phone className="w-3.5 h-3.5" /> SMS
                  </button>
                  <button type="button" onClick={() => { window.location.href = mailUrl; }}
                    className="flex items-center justify-center gap-1.5 py-2.5 bg-white/5 border border-white/15 text-white text-xs font-semibold rounded-lg hover:bg-white/10 transition-colors"
                    data-testid="share-email-btn">
                    <Share2 className="w-3.5 h-3.5" /> Email
                  </button>
                </div>

                {typeof referralInfo.points === 'number' && (
                  <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Award className="w-4 h-4 text-[#C97A2B]" />
                      <span className="text-white/70 text-xs">Your loyalty balance</span>
                    </div>
                    <span className="text-[#C97A2B] text-sm font-bold" data-testid="ref-loyalty-balance">{referralInfo.points} pts</span>
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {loading && (
          <div className="text-center py-12">
            <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              className="w-8 h-8 border-2 border-[#C97A2B] border-t-transparent rounded-full mx-auto" />
          </div>
        )}

        {!loading && (
          <>
            {/* Active Orders */}
            {activeOrders.length > 0 && (
              <div className="mb-8">
                <p className="text-[10px] tracking-[0.2em] uppercase text-[#C97A2B] font-medium mb-3">
                  Active Orders ({activeOrders.length})
                </p>
                <div className="space-y-3">
                  {activeOrders.map((order) => (
                    <motion.button key={order.id} onClick={() => setSelectedOrder(order)}
                      className="w-full bg-white rounded-xl border border-[#C97A2B]/20 p-4 text-left hover:shadow-md transition-all"
                      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                      data-testid={`order-${order.id?.slice(0, 8)}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[#2F2F2F] font-medium text-sm">#{order.id?.slice(0, 8).toUpperCase()}</span>
                        <span className="px-2.5 py-0.5 rounded-full text-[10px] tracking-wide uppercase font-medium"
                          style={{ backgroundColor: `${statusColor(order.status)}15`, color: statusColor(order.status) }}>
                          {statusLabel(order.status)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs text-[#6B705C]">
                        <span>{order.items?.length} item{order.items?.length !== 1 ? 's' : ''}</span>
                        <span className="text-[#2F2F2F] font-semibold">{order.totalPrice}</span>
                      </div>
                    </motion.button>
                  ))}
                </div>
              </div>
            )}

            {/* Past Orders */}
            {pastOrders.length > 0 && (
              <div>
                <p className="text-[10px] tracking-[0.2em] uppercase text-[#6B705C] mb-3">
                  Past Orders ({pastOrders.length})
                </p>
                <div className="space-y-3">
                  {pastOrders.map((order) => (
                    <button key={order.id} onClick={() => setSelectedOrder(order)}
                      className="w-full bg-white rounded-xl border border-[#D9D9D6] p-4 text-left hover:shadow-sm transition-all"
                      data-testid={`past-order-${order.id?.slice(0, 8)}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[#2F2F2F] font-medium text-sm">#{order.id?.slice(0, 8).toUpperCase()}</span>
                        <span className="px-2.5 py-0.5 rounded-full text-[10px] tracking-wide uppercase font-medium"
                          style={{ backgroundColor: `${statusColor(order.status)}15`, color: statusColor(order.status) }}>
                          {statusLabel(order.status)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs text-[#6B705C]">
                        <span>{order.orderDate || ''} · {order.items?.length} item{order.items?.length !== 1 ? 's' : ''}</span>
                        <span className="text-[#2F2F2F] font-semibold">{order.totalPrice}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {orders.length === 0 && !error && (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-[#D9D9D6] mx-auto mb-3" />
                <p className="text-[#2F2F2F] font-medium mb-1">No orders yet</p>
                <p className="text-[#6B705C] text-sm mb-4">Place your first order to see it here.</p>
                <button onClick={() => navigate('/order')} className="px-6 py-2.5 bg-[#C97A2B] text-white text-sm rounded-lg hover:bg-[#B65A3A] transition-colors">
                  Order Now
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CustomerPage;
